import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import ListingCard from "@/components/ListingCard";
import ListingCardSkeleton from "@/components/ListingCardSkeleton";
import AdSlot from "@/components/AdSlot";
import { Search, Filter, X } from "lucide-react";

const PAGE_SIZE = 24;

export default function DirectoryPage() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cats, setCats] = useState([]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const r = await api.get("/categories");
        if (active) setCats(r.data.categories || []);
      } catch { /* ignore */ }
    })();
    return () => { active = false; };
  }, []);

  const q = params.get("q") || "";
  const category = params.get("category") || "";
  const city = params.get("city") || "";
  const state = params.get("state") || "";
  const sub_category = params.get("sub_category") || "";
  const accepts_medicaid = params.get("accepts_medicaid") === "1";
  const accepts_medicare = params.get("accepts_medicare") === "1";
  const page = parseInt(params.get("page") || "1", 10);

  const updateParam = useCallback(
    (patch) => {
      const next = new URLSearchParams(params);
      Object.entries(patch).forEach(([k, v]) => {
        if (v === "" || v === false || v === null || v === undefined) next.delete(k);
        else next.set(k, v === true ? "1" : v);
      });
      if (!("page" in patch)) next.delete("page");
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  useEffect(() => {
    let active = true;
    setLoading(true);
    (async () => {
      try {
        const res = await api.get("/listings", {
          params: {
            q: q || undefined,
            category: category || undefined,
            sub_category: sub_category || undefined,
            city: city || undefined,
            state: state || undefined,
            accepts_medicaid: accepts_medicaid || undefined,
            accepts_medicare: accepts_medicare || undefined,
            page,
            page_size: PAGE_SIZE,
          },
        });
        if (active) setData(res.data);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [q, category, sub_category, city, state, accepts_medicaid, accepts_medicare, page]);

  const activeChips = useMemo(() => {
    const chips = [];
    if (q) chips.push({ label: `"${q}"`, key: "q" });
    if (category) chips.push({ label: category, key: "category" });
    if (city) chips.push({ label: city, key: "city" });
    if (state) chips.push({ label: state, key: "state" });
    if (accepts_medicaid) chips.push({ label: "Medicaid", key: "accepts_medicaid" });
    if (accepts_medicare) chips.push({ label: "Medicare", key: "accepts_medicare" });
    return chips;
  }, [q, category, city, state, accepts_medicaid, accepts_medicare]);

  const totalPages = data?.total_pages || 1;

  return (
    <div className="container-page py-10" data-testid="directory-page">
      <div className="mb-8">
        <span className="text-xs font-semibold uppercase tracking-[0.22em] text-accent-dark">Directory</span>
        <h1 className="mt-2 font-display text-4xl font-bold text-primary lg:text-5xl">Find your provider</h1>
        <p className="mt-2 text-foreground/70">
          {data?.total ?? "—"} resources across Colorado, filtered to fit you.
        </p>
      </div>

      {/* Search row */}
      <div className="card-surface flex flex-col gap-3 p-3 sm:flex-row" data-testid="directory-search-bar">
        <label className="flex w-full items-center gap-2 rounded-md bg-sand/60 px-3 py-2.5">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input
            defaultValue={q}
            placeholder="Search providers, services, conditions…"
            className="w-full bg-transparent outline-none"
            onKeyDown={(e) => {
              if (e.key === "Enter") updateParam({ q: e.target.value });
            }}
            data-testid="directory-q-input"
          />
        </label>
      </div>

      {/* MD sub-category chips (Medical Doctors only per Dr. Terri's Aug 14 meeting notes) */}
      {category === "Medical Doctors" && (
        <div className="mt-5 flex flex-wrap items-center gap-2 border-t border-border/50 pt-4" data-testid="sub-category-row">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Sub-category:</span>
          {["Internal Medicine", "Family Medicine", "Pediatrics",
            "Obstetrics/Gynecology", "Orthopedic Surgery", "Other"].map((sc) => (
            <button
              key={sc}
              onClick={() => updateParam({ sub_category: sub_category === sc ? "" : sc })}
              className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
                sub_category === sc
                  ? "bg-primary text-primary-foreground"
                  : "bg-primary/8 text-primary hover:bg-primary/15"
              }`}
              data-testid={`sub-cat-${sc.replace(/[^a-zA-Z]/g, "")}`}
            >
              {sc}
            </button>
          ))}
        </div>
      )}

      {/* Active chips */}
      {activeChips.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2" data-testid="active-filters">
          {activeChips.map((c) => (
            <button
              key={c.key}
              onClick={() => updateParam({ [c.key]: "" })}
              className="inline-flex items-center gap-1.5 rounded-full bg-primary/8 px-3 py-1 text-xs font-semibold text-primary hover:bg-primary/15"
              data-testid={`chip-${c.key}`}
            >
              {c.label} <X className="h-3 w-3" />
            </button>
          ))}
          <button
            onClick={() => setParams({}, { replace: true })}
            className="text-xs font-semibold text-muted-foreground hover:text-primary"
            data-testid="clear-filters"
          >
            Clear all
          </button>
        </div>
      )}

      <div className="mt-8 grid gap-8 lg:grid-cols-[280px_1fr]">
        {/* Filter rail */}
        <aside className="space-y-8" data-testid="filter-rail">
          <div>
            <div className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-foreground/80">
              <Filter className="h-4 w-4" /> Specialty
            </div>
            <ul className="space-y-1.5 text-sm">
              <li>
                <button
                  onClick={() => updateParam({ category: "" })}
                  className={`block w-full rounded-md px-2 py-1.5 text-left transition-colors ${!category ? "bg-primary/10 font-semibold text-primary" : "text-foreground/70 hover:text-primary"}`}
                  data-testid="filter-cat-all"
                >
                  All categories
                </button>
              </li>
              {cats.map((c) => (
                <li key={c.id || c.name}>
                  <button
                    onClick={() => updateParam({ category: c.name })}
                    className={`block w-full rounded-md px-2 py-1.5 text-left transition-colors ${category === c.name ? "bg-primary/10 font-semibold text-primary" : "text-foreground/70 hover:text-primary"}`}
                    data-testid={`filter-cat-${c.name}`}
                  >
                    {c.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <div className="mb-3 text-sm font-semibold uppercase tracking-wider text-foreground/80">Location</div>
            <input
              defaultValue={city}
              placeholder="City"
              className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-accent"
              onKeyDown={(e) => {
                if (e.key === "Enter") updateParam({ city: e.target.value });
              }}
              data-testid="filter-city"
            />
            <select
              value={state}
              onChange={(e) => updateParam({ state: e.target.value })}
              className="mt-2 w-full rounded-md border border-border bg-card px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-accent"
              data-testid="filter-state"
            >
              <option value="">All states</option>
              <option value="CO">Colorado</option>
            </select>
          </div>

          <div>
            <div className="mb-3 text-sm font-semibold uppercase tracking-wider text-foreground/80">Insurance</div>
            <label className="flex cursor-pointer items-center gap-2.5 py-1.5 text-sm" data-testid="filter-medicaid-label">
              <input
                type="checkbox"
                checked={accepts_medicaid}
                onChange={(e) => updateParam({ accepts_medicaid: e.target.checked })}
                className="h-4 w-4 accent-accent"
                data-testid="filter-medicaid"
              />
              Accepts Medicaid
            </label>
            <label className="flex cursor-pointer items-center gap-2.5 py-1.5 text-sm" data-testid="filter-medicare-label">
              <input
                type="checkbox"
                checked={accepts_medicare}
                onChange={(e) => updateParam({ accepts_medicare: e.target.checked })}
                className="h-4 w-4 accent-accent"
                data-testid="filter-medicare"
              />
              Accepts Medicare
            </label>
          </div>
          <div className="mt-2"><AdSlot placement="sidebar" /></div>
        </aside>

        {/* Results */}
        <section data-testid="directory-results">
          {loading ? (
            <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => <ListingCardSkeleton key={i} />)}
            </div>
          ) : data?.results?.length ? (
            <>
              <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
                {data.results.map((l) => <ListingCard key={l.id} listing={l} />)}
              </div>
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-10 flex flex-wrap items-center justify-center gap-2" data-testid="pagination">
                  <button
                    disabled={page <= 1}
                    onClick={() => updateParam({ page: Math.max(1, page - 1) })}
                    className="rounded-md border border-border bg-card px-3 py-1.5 text-sm font-medium text-primary disabled:opacity-40"
                    data-testid="page-prev"
                  >
                    ← Prev
                  </button>
                  <span className="text-sm text-muted-foreground" data-testid="page-status">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    disabled={page >= totalPages}
                    onClick={() => updateParam({ page: page + 1 })}
                    className="rounded-md border border-border bg-card px-3 py-1.5 text-sm font-medium text-primary disabled:opacity-40"
                    data-testid="page-next"
                  >
                    Next →
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="card-surface p-12 text-center" data-testid="empty-state">
              <h3 className="font-display text-2xl font-bold text-primary">No matches yet</h3>
              <p className="mt-2 text-foreground/70">Try fewer filters or a different search term.</p>
              <button onClick={() => setParams({}, { replace: true })} className="btn-secondary mt-6">
                Clear all filters
              </button>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
